import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { AccessModel } from '@jwp/ott-common/types/config';
import type { Playlist, PlaylistItem } from '@jwp/ott-common/types/playlist';
import { mediaURL } from '@jwp/ott-common/src/utils/urlFormatting';
import { Breakpoint, type Breakpoints } from '@jwp/ott-ui-react/src/hooks/useBreakpoint';
import BackArrowIcon from '@jwp/ott-theme/assets/icons/back_arrow.svg?react';

import Button from '../Button/Button';
import CardGrid from '../CardGrid/CardGrid';
import IconButton from '../IconButton/IconButton';
import Icon from '../Icon/Icon';

import styles from './Favorites.module.scss';
import { useTheme } from '../../../../../ThemeContext';

type Props = {
  playlist: Playlist;
  accessModel: AccessModel;
  hasSubscription: boolean;
  onCardHover?: (item: PlaylistItem) => void;
  onClearFavoritesClick: () => void;
};

const cols: Breakpoints = {
  [Breakpoint.xs]: 2,
  [Breakpoint.sm]: 3,
  [Breakpoint.md]: 5,
  [Breakpoint.lg]: 5,
  [Breakpoint.xl]: 5,
};

const Favorites = ({ playlist, accessModel, hasSubscription, onCardHover, onClearFavoritesClick }: Props): JSX.Element => {
  const { t } = useTranslation('user');
  const navigate = useNavigate(); // Initialize useNavigate
  const { universalStyles } = useTheme();

  const handleBackClick = () => {
    navigate(-1); // Navigate to the previous page
  };
  const getURL = (playlistItem: PlaylistItem) => mediaURL({ id: playlistItem.mediaid, title: playlistItem.title, playlistId: playlistItem.feedid });

  return (
    <div>
      <div className={styles.header}>
        <div className={styles.headerTitlePart}>
          <IconButton className={styles.iconButton} aria-controls="sidebar" aria-haspopup="true" onClick={handleBackClick}>
            <Icon icon={BackArrowIcon} />
          </IconButton>
          <h1>{t('favorites.title')}</h1>
        </div>
        {playlist.playlist.length > 0 ? (
          <Button type="button" variant="dangerText" size="large" label={t('favorites.clear')} onClick={onClearFavoritesClick} textStyle={universalStyles.deleteText}/>
        ) : null}
      </div>
      {playlist.playlist.length > 0 ? (
        <CardGrid
          getUrl={getURL}
          playlist={playlist}
          onCardHover={onCardHover}
          cols={cols}
          isLoading={false}
          accessModel={accessModel}
          isLoggedIn={true}
          hasSubscription={hasSubscription}
        />
      ) : (
        <p className={styles.emptyFavourites} style={universalStyles.primaryTextColor}>{t('favorites.no_favorites')}</p>
      )}
    </div>
  );
};

export default Favorites;
